import { ListingResource, UnitField } from '../../system/field';
import { BaseResourceManager } from '../BaseResourceManager';
import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { useReso } from '../../contexts/ResoContext';

export class UnitResource implements Record<string, any> {
  [key: string]: any;

  constructor(data: Record<string, any> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  getKey(): string | number | undefined {
    return this[UnitField.UNIT_KEY];
  }

  getItemNumber(): number | undefined {
    return this[UnitField.UNIT_TYPE_ITEM_NUMBER];
  }

  getListingKeyField(): string {
    return UnitField.UNIT_TYPE_LISTING_KEY;
  }

  getListingKey(): string | undefined {
    return this[UnitField.UNIT_TYPE_LISTING_KEY];
  }

  // Utility methods for unit resources

  /**
   * Get the unit type
   */
  getType(): string | null {
    return this[UnitField.UNIT_TYPE_TYPE] || null;
  }

  /**
   * Get the unit level
   */
  getLevel(): string | null {
    return this[UnitField.UNIT_TYPE_LEVEL] || null;
  }

  /**
   * Get the monthly rent
   */
  getMonthlyRent(): number | null {
    const rent = this[UnitField.UNIT_TYPE_MONTHLY_RENT];
    return rent !== undefined && rent !== null ? Number(rent) : null;
  }

  /**
   * Check if this is a pro forma unit
   */
  isProForma(): boolean {
    return this[UnitField.UNIT_TYPE_PRO_FORMA] === true;
  }

  /**
   * Check if this unit is occupied
   */
  isOccupied(): boolean {
    return this[UnitField.UNIT_TYPE_OCCUPIED_YN] === true;
  }

  /**
   * Get the finished square footage
   */
  getFinishedSqft(): number | null {
    const sqft = this[UnitField.UNIT_TYPE_FINISHED_SQFT];
    return sqft !== undefined && sqft !== null ? Number(sqft) : null;
  }

  /**
   * Get the lease type
   */
  getLeaseType(): string | null {
    return this[UnitField.UNIT_TYPE_LEASE_TYPE] || null;
  }

  /**
   * Get the lease expiration date
   */
  getLeaseExpirationDate(): Date | null {
    const dateStr = this[UnitField.UNIT_TYPE_LEASE_EXPIRATION_DATE];
    return dateStr ? new Date(dateStr) : null;
  }

  /**
   * Get the total beds
   */
  getBedsTotal(): number | null {
    const beds = this[UnitField.UNIT_TYPE_BEDS_TOTAL];
    return beds !== undefined && beds !== null ? Number(beds) : null;
  }

  /**
   * Get the total baths
   */
  getBathsTotal(): number | null {
    const baths = this[UnitField.UNIT_TYPE_BATHS_TOTAL];
    return baths !== undefined && baths !== null ? Number(baths) : null;
  }

  /**
   * Get full baths
   */
  getFullBaths(): number | null {
    const baths = this[UnitField.UNIT_TYPE_BATHS_FULL];
    return baths !== undefined && baths !== null ? Number(baths) : null;
  }

  /**
   * Get half baths
   */
  getHalfBaths(): number | null {
    const baths = this[UnitField.UNIT_TYPE_BATHS_HALF];
    return baths !== undefined && baths !== null ? Number(baths) : null;
  }

  /**
   * Get the total rooms
   */
  getTotalRooms(): number | null {
    const rooms = this[UnitField.UNIT_TYPE_TOTAL_ROOMS];
    return rooms !== undefined && rooms !== null ? Number(rooms) : null;
  }

  /**
   * Get unit features as an array
   */
  getFeatures(): string[] {
    const features = this[UnitField.UNIT_TYPE_FEATURES];
    if (!features) return [];

    if (Array.isArray(features)) {
      return features;
    }

    if (typeof features === 'string') {
      return features.split(',').map((feature) => feature.trim());
    }

    return [];
  }

  /**
   * Check if space is contiguous
   */
  isContiguous(): boolean {
    return this[UnitField.UNIT_TYPE_CONTIGUOUS_SPACE_YN] === true;
  }

  /**
   * Get security deposit amount
   */
  getSecurityDeposit(): number | null {
    const deposit = this[UnitField.UNIT_TYPE_SECURITY_DEPOSIT];
    return deposit !== undefined && deposit !== null ? Number(deposit) : null;
  }
}

export class UnitResourceManager extends BaseResourceManager<UnitResource> {
  constructor(
    resourceType: ListingResource,
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: {
      enableCwa?: boolean;
      useTranslation?: boolean;
    } = {},
  ) {
    super(resourceType, executeQuery, fieldTranslator, taskManager, options);
  }

  /**
   * Core mapping function from API data to UnitResource
   */
  protected mapFromApiCore(apiData: any): UnitResource {
    return new UnitResource(apiData);
  }

  /**
   * Core mapping function from UnitResource to API data
   */
  protected mapToApiCore(resource: UnitResource): any {
    // Return a clean copy of the resource
    return { ...resource };
  }

  /**
   * Filter units by type
   */
  public async getByType(
    unitType: string,
    listingKey?: string,
  ): Promise<UnitResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getByType' },
      async (_taskId) => {
        try {
          // Get all units
          const allUnits = await this.list(listingKey);

          // Filter by unit type
          return allUnits.filter(
            (unit) => unit[UnitField.UNIT_TYPE_TYPE] === unitType,
          );
        } catch (error) {
          console.error('Error getting units by type:', error);
          return [];
        }
      },
    );
  }

  /**
   * Get vacant units
   */
  public async getVacant(listingKey?: string): Promise<UnitResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getVacant' },
      async (_taskId) => {
        try {
          // Get all units
          const allUnits = await this.list(listingKey);

          // Filter for vacant units
          return allUnits.filter(
            (unit) => unit[UnitField.UNIT_TYPE_OCCUPIED_YN] !== true,
          );
        } catch (error) {
          console.error('Error getting vacant units:', error);
          return [];
        }
      },
    );
  }

  /**
   * Get occupied units
   */
  public async getOccupied(listingKey?: string): Promise<UnitResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getOccupied' },
      async (_taskId) => {
        try {
          // Get all units
          const allUnits = await this.list(listingKey);

          // Filter for occupied units
          return allUnits.filter(
            (unit) => unit[UnitField.UNIT_TYPE_OCCUPIED_YN] === true,
          );
        } catch (error) {
          console.error('Error getting occupied units:', error);
          return [];
        }
      },
    );
  }

  /**
   * Get units with specified bed count
   */
  public async getByBedCount(
    bedCount: number,
    listingKey?: string,
  ): Promise<UnitResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getByBedCount' },
      async (_taskId) => {
        try {
          // Get all units
          const allUnits = await this.list(listingKey);

          // Filter by bed count
          return allUnits.filter(
            (unit) => Number(unit[UnitField.UNIT_TYPE_BEDS_TOTAL]) === bedCount,
          );
        } catch (error) {
          console.error('Error getting units by bed count:', error);
          return [];
        }
      },
    );
  }

  /**
   * Get units sorted by monthly rent
   */
  public async getSortedByRent(
    ascending = true,
    listingKey?: string,
  ): Promise<UnitResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'getSortedByRent' },
      async (_taskId) => {
        try {
          // Get all units
          const allUnits = await this.list(listingKey);

          // Sort by monthly rent
          return [...allUnits].sort((a, b) => {
            const rentA = a.getMonthlyRent() || 0;
            const rentB = b.getMonthlyRent() || 0;
            return ascending ? rentA - rentB : rentB - rentA;
          });
        } catch (error) {
          console.error('Error getting units sorted by rent:', error);
          return [];
        }
      },
    );
  }
}

/**
 * Create a new UnitResource with required fields
 */
export function createUnitResource(
  data: Partial<Record<string, any>> = {},
): UnitResource {
  const defaults = {
    [UnitField.UNIT_TYPE_ITEM_NUMBER]: 0,
  };

  return new UnitResource({
    ...defaults,
    ...data,
  });
}
